/**
 * period-picker — the one control for naming a period.
 *
 * A row of mode toggles over the current mode's fields. Fully controlled: it
 * holds no state and emits a PATCH of the fields that changed, so the caller's
 * period is the only copy and switching modes preserves everything typed under
 * the others.
 *
 * The caller decides WHICH modes its question allows and nothing else. How a
 * month is entered, that a quarter needs a year beside it, which field belongs
 * to which mode — all of that is this unit's, in one place, so the filter bar
 * and the upload form cannot drift into two different ways of typing a date.
 *
 * Every field value is ISO and the mode row is the only chrome: no panel, no
 * title, no border. A host that needs a box puts this in one.
 *
 * ⛔ A COARSE PERIOD IS CHOSEN, NOT TYPED. Only a single DAY is free enough to
 * be worth a keyboard; twelve months and a handful of plausible years are
 * picked from a list. The native month input was a picker in some browsers and
 * a plain text box in the rest, and no native year input exists at all.
 *
 * THE DAY IS THE ONE VALUE WORTH A KEYBOARD, and it gets `shared/ui/DateField`
 * rather than the native `<input type="date">` — that control carries the
 * reason.
 */
import type { ReactElement } from 'react'
import { useT } from 'flyt/lib/language/language'
import { formatDate, formatMonth } from 'flyt/lib/formatDate/formatDate'
import { Button } from 'flyt/ui/Button/Button'
import { DateField } from 'flyt/ui/DateField/DateField'
import { AkselNativeSelect as NativeSelect } from 'flyt/ui/AkselForm/AkselForm'
import type { Period, PeriodMode, PeriodQuarter, PeriodPickerProps } from './PeriodPicker.types'

/** Each mode's already-owned label key — the vocabulary is this unit's, not a caller's. */
const MODE_LABEL_KEYS: Readonly<Record<PeriodMode, string>> = {
  date: 'filter.mode.date',
  month: 'filter.mode.month',
  quarter: 'filter.mode.quarter',
  year: 'filter.mode.year',
  range: 'filter.mode.range',
}

const QUARTERS: readonly PeriodQuarter[] = ['Q1', 'Q2', 'Q3', 'Q4']

/** The ISO month parts, in order — the stored value; the label comes from a key. */
const MONTHS: readonly string[] = Array.from({ length: 12 }, (_, index) =>
  String(index + 1).padStart(2, '0'),
)

/**
 * How many years the list offers, the current one included. ⚠️ A backend will
 * likely serve this window one day, so it is ONE named value rather than a rule
 * spread over the fields that use it.
 */
const YEAR_SPAN = 5

/**
 * The years to offer, newest first. A year already HELD outside the window (an
 * older document, a restored filter) is appended: a list that cannot show the
 * value it holds would read as empty while the field is full.
 */
const yearChoices = (selected: string): readonly string[] => {
  const current = new Date().getFullYear()
  const recent = Array.from({ length: YEAR_SPAN }, (_, index) => String(current - index))
  return selected === '' || recent.includes(selected) ? recent : [...recent, selected]
}

/** The two halves of an ISO month (`YYYY-MM`); each is empty when unset. */
const monthParts = (month: string | undefined): readonly [string, string] => {
  const [year = '', monthOfYear = ''] = (month ?? '').split('-')
  return [year, monthOfYear]
}

/** Which modes show a given field — the pairing rule, stated once. */
const showsYear = (mode: PeriodMode): boolean =>
  mode === 'quarter' || mode === 'year' || mode === 'month'

/**
 * How a period READS on a closed trigger — the summary beside a label, in the
 * current mode's own terms. It lives with the control that edits the period so
 * a filter chip and an upload field can never spell the same period two ways.
 *
 * ⚠️ IT READS IN THE PORTAL'S DATE ORDER, not in the storage order: handing
 * back the stored ISO verbatim put `2026-08-24` on a filter chip beside
 * `24.08.2026` in the table it was filtering. Presentation is
 * `shared/lib/formatDate`'s job. An empty string means the mode holds nothing.
 */
export const periodSummary = (period: Period): string => {
  switch (period.mode) {
    case 'date':
      return period.date === undefined ? '' : formatDate(period.date)
    case 'month':
      return period.month === undefined ? '' : formatMonth(period.month)
    case 'quarter':
      return period.quarter === undefined && period.year === undefined
        ? ''
        : `${period.quarter ?? ''} ${period.year ?? ''}`.trim()
    case 'year':
      return period.year ?? ''
    case 'range':
      return period.from === undefined && period.to === undefined
        ? ''
        : `${period.from === undefined ? '…' : formatDate(period.from)} – ${
            period.to === undefined ? '…' : formatDate(period.to)
          }`
  }
}

export function PeriodPicker({ period, modes, onPatch }: PeriodPickerProps): ReactElement {
  const t = useT('common')

  // In the month mode the composed `YYYY-MM` is the truth about both halves;
  // in the others the year IS the value. Reading it back this way keeps a
  // month restored from elsewhere (a shared filter, a saved upload) showing
  // the year it actually holds.
  const [composedYear, monthOfYear] = monthParts(period.month)
  const selectedYear = (period.mode === 'month' ? composedYear : '') || (period.year ?? '')

  const patchYear = (year: string): void => {
    if (period.mode !== 'month') {
      onPatch({ year })
      return
    }
    // Here the year is HALF of one value: clearing it clears the period, and
    // changing it moves the month already chosen to the new year.
    onPatch({ year, month: year === '' || monthOfYear === '' ? '' : `${year}-${monthOfYear}` })
  }

  const patchMonth = (chosen: string): void => {
    if (chosen === '') {
      onPatch({ month: '' })
      return
    }
    // A month with no year is not a period. The current year is what a reader
    // means by default, and writing it back SHOWS that in the year list rather
    // than hiding it inside the value.
    const year = selectedYear === '' ? String(new Date().getFullYear()) : selectedYear
    onPatch({ year, month: `${year}-${chosen}` })
  }

  return (
    <div className="flex flex-col gap-2">
      {/* One offered mode is nothing to switch between, so the row is absent
          entirely — the same reasoning as a picker with one option. */}
      {modes.length > 1 ? (
        <div className="flex flex-wrap gap-1.5">
          {modes.map((mode) => (
            <Button
              key={mode}
              variant={period.mode === mode ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => {
                onPatch({ mode })
              }}
            >
              {t(MODE_LABEL_KEYS[mode])}
            </Button>
          ))}
        </div>
      ) : null}
      {period.mode === 'date' ? (
        <PeriodField label={t('filter.field.date')}>
          <DateField
            value={period.date ?? ''}
            onChange={(date) => {
              onPatch({ date })
            }}
          />
        </PeriodField>
      ) : null}
      {/* Coarse before fine, the way the reader narrows: the year, then the
          month inside it — the same order the quarter mode reads in. */}
      {showsYear(period.mode) ? (
        <PeriodField label={t('filter.field.year')}>
          <NativeSelect label={t('filter.field.year')}
            value={selectedYear}
            onChange={(event) => {
              patchYear(event.target.value)
            }}
          >
            <option value="">{t('filter.choose')}</option>
            {yearChoices(selectedYear).map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </NativeSelect>
        </PeriodField>
      ) : null}
      {period.mode === 'month' ? (
        <PeriodField label={t('filter.field.month')}>
          <NativeSelect label={t('filter.field.month')}
            value={monthOfYear}
            onChange={(event) => {
              patchMonth(event.target.value)
            }}
          >
            <option value="">{t('filter.choose')}</option>
            {MONTHS.map((month) => (
              // The stored value stays the ISO part; only the LABEL is named,
              // through a key like every other string in the portal.
              <option key={month} value={month}>
                {t(`month.${month}`)}
              </option>
            ))}
          </NativeSelect>
        </PeriodField>
      ) : null}
      {period.mode === 'quarter' ? (
        <div className="grid grid-cols-4 gap-1.5">
          {QUARTERS.map((quarter) => (
            <Button
              key={quarter}
              variant={period.quarter === quarter ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => {
                onPatch({ quarter })
              }}
            >
              {quarter}
            </Button>
          ))}
        </div>
      ) : null}
      {period.mode === 'range' ? (
        <div className="flex gap-2">
          <PeriodField label={t('filter.field.from')}>
            <DateField
              value={period.from ?? ''}
              onChange={(from) => {
                onPatch({ from })
              }}
            />
          </PeriodField>
          <PeriodField label={t('filter.field.to')}>
            <DateField
              value={period.to ?? ''}
              onChange={(to) => {
                onPatch({ to })
              }}
            />
          </PeriodField>
        </div>
      ) : null}
    </div>
  )
}

interface PeriodFieldProps {
  label: string
  children: ReactElement
}

/** One field under its micro-label; the label names the field for its control. */
function PeriodField({ label, children }: PeriodFieldProps): ReactElement {
  return (
    <label className="flex flex-1 flex-col gap-1 text-xs font-semibold tracking-wide text-text-muted uppercase">
      {label}
      {children}
    </label>
  )
}

export type { Period, PeriodMode, PeriodQuarter }
